import { MouseEventHandler, useCallback } from 'react';
import type { BaseRowData } from '@adv-frontend/data-provider';

import type { ColumnSizingState } from '../types/tanstack-table-types';

import type { GridHeader, GridTable } from '../types';

const DEFAULT_MIN_SIZE = 30;

/**
 * Хук для получения обработчика ресайза колонки.
 * Является копией https://github.com/TanStack/table/blob/88977fa0aa9d714000069515f340e25a82c9217c/packages/table-core/src/features/ColumnSizing.ts#L191
 * С доработкой, которая учитывает минимальные и максимальные размеры колонок.
 *
 * @typeParam RowData Данные строки.
 * @param grid Данные для грида.
 * @param header Заголовок колонки грида.
 * @param gridWidth Ширина грида.
 * @returns Обработчик события ресайза колонки
 */
export const useGridResizeHandler = <RowData extends BaseRowData>(
    grid: GridTable<RowData>,
    header: GridHeader<RowData>,
    gridWidth: number = Number.MAX_SAFE_INTEGER
): MouseEventHandler<HTMLDivElement> => {
    return useCallback(
        e => {
            const column = grid.getColumn(header.column.id);
            const canResize = column?.getCanResize();
            if (!column || !canResize) {
                return;
            }

            const columnSize = column.getSize();
            const defaultColumnSize = column.columnDef.size;
            if (e.detail === 2 && defaultColumnSize && defaultColumnSize !== columnSize) {
                grid.setColumnSizing(old => ({ ...old, [column.id]: defaultColumnSize }));
                return;
            }

            const startSize = header.getSize();

            const columnSizingStart: [string, number][] = header
                ? header.getLeafHeaders().map(d => [d.column.id, d.column.getSize()])
                : [[column.id, column.getSize()]];

            const clientX = e.clientX;

            const newColumnSizing: ColumnSizingState = {};

            const updateOffset = (eventType: 'move' | 'end', clientXPos?: number) => {
                if (typeof clientXPos !== 'number') {
                    return;
                }

                const { minSize = DEFAULT_MIN_SIZE, maxSize = gridWidth } = header.column.columnDef;
                const deltaMinOffset = minSize - startSize;
                const deltaMaxOffset = maxSize - startSize;

                const mouseDeltaOffset = clientXPos - clientX;
                const deltaOffset = Math.min(Math.max(deltaMinOffset, mouseDeltaOffset), deltaMaxOffset);
                const deltaPercentage = Math.max(deltaOffset / (startSize ?? 0), -0.999999);

                columnSizingStart.forEach(([columnId, headerSize]) => {
                    const newColumnSize =
                        Math.round(Math.max(headerSize + headerSize * deltaPercentage, 0) * 100) / 100;
                    if (newColumnSize !== headerSize) {
                        newColumnSizing[columnId] = newColumnSize;
                    }
                });

                grid.setColumnSizingInfo(old => ({
                    ...old,
                    deltaOffset,
                    deltaPercentage,
                }));

                const hasColumnSizingChanges = Object.keys(newColumnSizing).length > 0;
                if ((grid.options.columnResizeMode === 'onChange' || eventType === 'end') && hasColumnSizingChanges) {
                    grid.setColumnSizing(old => ({
                        ...old,
                        ...newColumnSizing,
                    }));
                }
            };

            const onMove = (clientXPos?: number) => updateOffset('move', clientXPos);

            const onEnd = (clientXPos?: number) => {
                updateOffset('end', clientXPos);

                grid.setColumnSizingInfo(old => ({
                    ...old,
                    isResizingColumn: false,
                    startOffset: null,
                    startSize: null,
                    deltaOffset: null,
                    deltaPercentage: null,
                    columnSizingStart: [],
                }));
            };

            const mouseEvents = {
                moveHandler: (e: MouseEvent) => {
                    // Убираем выделение текста при перетаскивании ресайзера
                    e.preventDefault();
                    onMove(e.clientX);
                },
                upHandler: (e: MouseEvent) => {
                    document.removeEventListener('mousemove', mouseEvents.moveHandler);
                    document.removeEventListener('mouseup', mouseEvents.upHandler);
                    onEnd(e.clientX);
                },
            };

            document.addEventListener('mousemove', mouseEvents.moveHandler, { passive: false });
            document.addEventListener('mouseup', mouseEvents.upHandler, { passive: false });

            grid.setColumnSizingInfo(old => ({
                ...old,
                startOffset: clientX,
                startSize,
                deltaOffset: 0,
                deltaPercentage: 0,
                columnSizingStart,
                isResizingColumn: column.id,
            }));
        },
        [grid, header, gridWidth]
    );
};
